import React, { useMemo, useState } from 'react';
import {
  AdminPageHeader,
  AdminTableWrap,
  AdminDevRowMeta,
  AdminDevTh,
  btnLink,
  btnPrimary,
  filtersRowClass,
  selectClass,
  tdClass,
} from '../adminUi';
import { useGrading } from './GradingContext';
import { GradingDeleteButton, GradingVisibilityBadge } from './GradingShared';
import { compareDiffFactorOrder, confirmAndRunDelete } from './utils';
import { deleteGradingDiffFactor, reorderGradingDiffFactors } from '../../../../services/gradingService';

const FACTORS_FAMILY_FILTER_KEY = 'dc.admin.grading.factorsFamilyFilter';

function readFactorsFamilyFilter(): string {
  try {
    return sessionStorage.getItem(FACTORS_FAMILY_FILTER_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeFactorsFamilyFilter(value: string) {
  try {
    if (value) sessionStorage.setItem(FACTORS_FAMILY_FILTER_KEY, value);
    else sessionStorage.removeItem(FACTORS_FAMILY_FILTER_KEY);
  } catch {
    // ignore storage errors
  }
}

export default function GradingFactorsTab() {
  const { families, roleClusters, factors, questions, openDrawer, reload } = useGrading();
  const [familyFilter, setFamilyFilterState] = useState(readFactorsFamilyFilter);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);

  const rows = useMemo(() => {
    return factors
      .map((f) => {
        const family = families.find((x) => x.id === f.family_id);
        const familyRoleClusters = roleClusters.filter((s) => s.family_id === f.family_id);
        const qCount = questions.filter((q) => q.diff_factor_id === f.id).length;
        const roleClusterCount = new Set(
          questions.filter((q) => q.diff_factor_id === f.id).map((q) => q.role_cluster_id),
        ).size;
        return { f, family, familyRoleClusters, qCount, roleClusterCount };
      })
      .filter(({ f }) => !familyFilter || Number(f.family_id) === Number(familyFilter))
      .sort((a, b) => compareDiffFactorOrder(a.f, b.f));
  }, [factors, families, roleClusters, questions, familyFilter]);

  const canReorder = Boolean(familyFilter) && rows.length > 1;

  const setFamilyFilter = (value: string) => {
    setFamilyFilterState(value);
    writeFactorsFamilyFilter(value);
  };

  const persistOrder = async (orderedIds: number[]) => {
    if (!familyFilter) return;
    setReorderSaving(true);
    setReorderError(null);
    try {
      await reorderGradingDiffFactors(Number(familyFilter), orderedIds);
      await reload({ silent: true });
    } catch (error) {
      setReorderError(error instanceof Error ? error.message : 'Не удалось сохранить порядок');
    } finally {
      setReorderSaving(false);
      setDraggingId(null);
      setDropTargetId(null);
    }
  };

  const handleDrop = async (targetId: number) => {
    if (!canReorder || draggingId == null || draggingId === targetId) return;
    const orderedIds = rows.map((row) => row.f.id);
    const fromIndex = orderedIds.indexOf(draggingId);
    const toIndex = orderedIds.indexOf(targetId);
    if (fromIndex < 0 || toIndex < 0) return;
    const nextIds = [...orderedIds];
    nextIds.splice(fromIndex, 1);
    nextIds.splice(toIndex, 0, draggingId);
    await persistOrder(nextIds);
  };

  return (
    <div>
      <AdminPageHeader
        title="Дифференцирующие факторы"
        subtitle={
          canReorder
            ? 'Перетащите строки, чтобы задать порядок вопросов в грейдинге, матрице и сообществе'
            : 'Единый набор факторов на Family; для каждой Role cluster может быть несколько вопросов на фактор'
        }
        actions={
          <button type="button" className={btnPrimary} onClick={() => openDrawer({ type: 'factor', mode: 'create', familyId: families[0]?.id })}>
            Создать диффактор
          </button>
        }
      />

      <div className={filtersRowClass}>
        <select className={selectClass} value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}>
          <option value="">Все Family</option>
          {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>

      {!familyFilter && (
        <p className="dc-grading-reorder-hint">
          Выберите Family в фильтре, чтобы изменить порядок факторов drag-and-drop.
        </p>
      )}

      {reorderError && <p className="dc-grading-reorder-error">{reorderError}</p>}
      {reorderSaving && <p className="dc-grading-reorder-hint">Сохраняем порядок…</p>}

      <AdminTableWrap>
        <table className="w-full min-w-[800px] text-sm">
          <thead className="border-b border-slate-100 bg-slate-50/80">
            <tr>
              {canReorder && <AdminDevTh label="" />}
              <AdminDevTh label="№" dbField="diff_factors.sort_order" />
              <AdminDevTh label="Название" dbField="diff_factors.name" />
              <AdminDevTh label="Family" dbField="diff_factors.family_id → families" />
              <AdminDevTh label="Вопросов" dbField="questions.diff_factor_id" />
              <AdminDevTh label="Role cluster покрыто" dbField="questions.role_cluster_id" />
              <AdminDevTh label="Видимость" dbField="diff_factors.is_visible" />
              <AdminDevTh label="Подсказка" dbField="diff_factors.question_hint" />
              <AdminDevTh label="Изменён" dbField="diff_factors.updated_at" />
              <AdminDevTh label="Действия" />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ f, family, qCount, roleClusterCount }, index) => (
              <tr
                key={f.id}
                className={`border-b border-slate-50 hover:bg-slate-50/50 ${
                  dropTargetId === f.id ? 'dc-grading-factor-row--drop-target' : ''
                } ${draggingId === f.id ? 'dc-grading-factor-row--dragging' : ''}`}
                draggable={canReorder && !reorderSaving}
                onDragStart={() => {
                  setDraggingId(f.id);
                  setReorderError(null);
                }}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDropTargetId(null);
                }}
                onDragOver={(event) => {
                  if (!canReorder || reorderSaving) return;
                  event.preventDefault();
                  setDropTargetId(f.id);
                }}
                onDragLeave={() => {
                  if (dropTargetId === f.id) setDropTargetId(null);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  void handleDrop(f.id);
                }}
              >
                {canReorder && (
                  <td className={`${tdClass} w-10 text-slate-400`}>
                    <span className="dc-grading-drag-handle" aria-hidden="true">⋮⋮</span>
                  </td>
                )}
                <td className={tdClass}>{f.sort_order ?? index + 1}</td>
                <td className={tdClass}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" className={btnLink} onClick={() => openDrawer({ type: 'factor', mode: 'edit', id: f.id })}>
                      {f.name}
                    </button>
                    <GradingVisibilityBadge isVisible={f.is_visible} />
                  </div>
                  <AdminDevRowMeta parts={[`diff_factors.id=${f.id}`, `diff_factors.family_id=${f.family_id}`]} />
                  {f.description && <p className="text-xs text-slate-500 mt-0.5">{f.description}</p>}
                </td>
                <td className={tdClass}>{family?.name || '—'}</td>
                <td className={tdClass}>{qCount}</td>
                <td className={tdClass}>{roleClusterCount}</td>
                <td className={tdClass}>{f.is_visible === false ? 'Скрыто' : 'Видно'}</td>
                <td className={`${tdClass} max-w-xs truncate text-slate-600`}>{f.question_hint || '—'}</td>
                <td className={tdClass}>{new Date(f.updated_at).toLocaleDateString('ru-RU')}</td>
                <td className={tdClass}>
                  <div className="flex gap-3">
                    <button type="button" className={btnLink} onClick={() => openDrawer({ type: 'factor', mode: 'edit', id: f.id })}>Редактировать</button>
                    <GradingDeleteButton
                      onClick={() => confirmAndRunDelete(
                        f.name,
                        'Будут удалены все scoring-вопросы этого фактора.',
                        () => deleteGradingDiffFactor(f.id),
                        reload,
                      )}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableWrap>
    </div>
  );
}
